import { Dialog, FlexColumn } from '@elementary/common-ui/components';
import { useForm } from 'react-hook-form';
import useCustomAlertOverride from 'shared/hooks/useCustomAlertOverride';
import { DestinationSection } from 'shared/components/AlertDestinations';
import { AlertRuleFormDestination } from 'shared/types';
import { ReactComponent as DestinationIcon } from '@elementary/common-ui/assets/20x20/destination.svg';
import OverrideFormExplanation from './OverrideFormExplanation';

type Props = {
  open: boolean;
  onClose: () => void;
  selectedTests: string[];
};

const AlertDestinationOverrideDialog = ({
  open,
  onClose,
  selectedTests = [],
}: Props) => {
  const methods = useForm<{ destinations: AlertRuleFormDestination[] }>({
    defaultValues: {
      destinations: [],
    },
  });

  const destinations = methods.watch('destinations');
  const selectedTestsCount = selectedTests.length;

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  const {
    customAlertOverride: {
      isCreateCustomAlertOverrideLoading,
      createCustomAlertOverrideMutation,
    },
  } = useCustomAlertOverride();

  const applyChanges = async () => {
    const destinations = methods.getValues('destinations');

    await createCustomAlertOverrideMutation({
      alertOverride: destinations.map((dest) => ({
        channelName: dest.channelName,
        destination: dest.type,
      })),
      entityIds: selectedTests,
    });
    handleClose();
  };

  return (
    <Dialog
      open={open}
      variant="error"
      titleIcon={<DestinationIcon />}
      title="Alert Destinations"
      onClose={handleClose}
      onConfirmButtonClick={applyChanges}
      confirmText={'Apply'}
      hideCancelButton
      isLoading={isCreateCustomAlertOverrideLoading}
      confirmDisabled={!destinations.length}
      footerMessage={
        'By clicking ״Apply״, the changes you made will update immediately'
      }
      customWidth={704}
      message={
        <FlexColumn sx={{ mb: '56px', gap: 2 }}>
          <OverrideFormExplanation
            testsCount={selectedTestsCount}
            destinations={destinations}
          />
          <DestinationSection
            destinations={destinations}
            setDestinationsValue={(destinations: AlertRuleFormDestination[]) =>
              methods.setValue(
                'destinations',
                destinations.map((dest) => ({
                  ...dest,
                  configurationType: 'CUSTOM',
                })),
              )
            }
          />
        </FlexColumn>
      }
    />
  );
};

export default AlertDestinationOverrideDialog;
